import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { StatusLabel } from '..';
import { formatSourceName, getSourceColor } from '../../utils';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { getFaviconDetectionsApi } from '../../services/singleThreatApi';
import { FaviconDetection } from '../../context/SingleThreat/SingleThreatType';

interface SourceFaviconChipProps {
  source: string;
  threatId: number;
  domainId: number;
}

const FAVICON_SOURCE = 'favicon_detection';

const SourceFaviconChip = ({
  source,
  threatId,
  domainId,
}: SourceFaviconChipProps) => {
  const faviconEnabled = useFeatureFlag('favicon-detection');
  const [faviconImage, setFaviconImage] = useState<string | null>(null);

  useEffect(() => {
    // Kill switch: when the flag is off, never call the favicon service or render
    // its imagery, so a broken backend contract can't surface errors here.
    if (
      !faviconEnabled ||
      source !== FAVICON_SOURCE ||
      !threatId ||
      !domainId
    ) {
      return;
    }

    const controller = new AbortController();
    const fetchFavicon = async () => {
      try {
        const res = await getFaviconDetectionsApi(
          threatId,
          domainId,
          controller.signal,
        );
        const detections: FaviconDetection[] = res.data?.detections ?? [];
        const matched = detections.find(
          detection => detection.matchedFaviconImage?.base64,
        );
        if (matched?.matchedFaviconImage?.base64) {
          const { mimeType, base64 } = matched.matchedFaviconImage;
          setFaviconImage(`data:${mimeType || 'image/png'};base64,${base64}`);
        }
      } catch (err) {
        // Favicon imagery is supplementary — fail silently and just show the chip.
      }
    };

    fetchFavicon();
    return () => controller.abort();
  }, [faviconEnabled, source, threatId, domainId]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <StatusLabel
        label={formatSourceName(source)}
        bgcolor={getSourceColor(source)}
      />
      {faviconEnabled && faviconImage && (
        <Box
          component="img"
          data-testid="threat-source-favicon"
          src={faviconImage}
          alt="Detected favicon"
          sx={{ width: 20, height: 20, borderRadius: '4px', flexShrink: 0 }}
        />
      )}
    </Box>
  );
};

export default SourceFaviconChip;
